import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Alert, Button, Card, CardBody, Checkbox, MainLayout, TextInput } from '../components';
import { useAppNavigation } from '../contexts/AppNavigationContext';
import {
  adminPrivacyAPI,
  AdminPrivacySettingsDto,
  AdminUserPrivacyPermissionsDto,
} from '../services/api';
import { brandClasses } from '../theme';

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'N/A' : `${parsed.toLocaleDateString()} ${parsed.toLocaleTimeString()}`;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: string; detail?: string; message?: string } } }).response;
    return response?.data?.detail || response?.data?.message || response?.data?.error || fallback;
  }

  return fallback;
};

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { clearNavigationState } = useAppNavigation();
  const systemSettingsRef = useRef<HTMLDivElement | null>(null);
  const userManagementRef = useRef<HTMLDivElement | null>(null);
  const hasLoadedInitialDataRef = useRef(false);

  const [settings, setSettings] = useState<AdminPrivacySettingsDto | null>(null);
  const [users, setUsers] = useState<AdminUserPrivacyPermissionsDto[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [savingSystemSetting, setSavingSystemSetting] = useState(false);
  const [savingUserId, setSavingUserId] = useState<string>('');

  useEffect(() => {
    clearNavigationState();
  }, [clearNavigationState]);

  useEffect(() => {
    if (location.hash === '#system-settings') {
      systemSettingsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    if (location.hash === '#user-management') {
      userManagementRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  const loadSettings = useCallback(async () => {
    try {
      setLoadingSettings(true);
      const response = await adminPrivacyAPI.getSettings();
      setSettings(response.data);
    } catch (loadError) {
      setError(getErrorMessage(loadError, t('errors.loadSettings', { ns: 'admin' })));
    } finally {
      setLoadingSettings(false);
    }
  }, [t]);

  const loadUsers = useCallback(async (query?: string) => {
    try {
      setLoadingUsers(true);
      const response = await adminPrivacyAPI.searchUserPermissions(query, 20);
      setUsers(response.data);
    } catch (loadError) {
      setError(getErrorMessage(loadError, t('errors.loadUsers', { ns: 'admin' })));
    } finally {
      setLoadingUsers(false);
    }
  }, [t]);

  useEffect(() => {
    if (hasLoadedInitialDataRef.current) {
      return;
    }

    hasLoadedInitialDataRef.current = true;
    void loadSettings();
    void loadUsers();
  }, [loadSettings, loadUsers]);

  const handleSystemToggle = async (nextValue: boolean) => {
    try {
      setSavingSystemSetting(true);
      const response = await adminPrivacyAPI.updateSettings({
        allowLivingPersonPublicDisplay: nextValue,
      });
      setSettings(response.data);
      setInfoMessage(
        nextValue
          ? t('messages.systemEnabled', { ns: 'admin' })
          : t('messages.systemDisabled', { ns: 'admin' })
      );
    } catch (saveError) {
      setError(getErrorMessage(saveError, t('errors.saveSettings', { ns: 'admin' })));
    } finally {
      setSavingSystemSetting(false);
    }
  };

  const handleUserToggle = async (userId: string, nextValue: boolean) => {
    try {
      setSavingUserId(userId);
      const response = await adminPrivacyAPI.updateUserPermissions(userId, {
        canAllowLivingPersonPublicDisplay: nextValue,
      });

      setUsers((current) =>
        current.map((user) => (user.userId === userId ? response.data : user))
      );
      setInfoMessage(
        nextValue
          ? t('messages.userEnabled', { ns: 'admin' })
          : t('messages.userDisabled', { ns: 'admin' })
      );
    } catch (saveError) {
      setError(getErrorMessage(saveError, t('errors.saveUser', { ns: 'admin' })));
    } finally {
      setSavingUserId('');
    }
  };

  const handleSearchSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadUsers(searchQuery);
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className={brandClasses.heroCard}>
          <h1 className={`text-3xl font-bold ${brandClasses.titleText}`}>{t('title', { ns: 'admin' })}</h1>
          <p className={`mt-2 text-sm ${brandClasses.mutedText}`}>
            {t('subtitle', { ns: 'admin' })}
          </p>
        </div>

        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}
        {infoMessage && <Alert variant="success" message={infoMessage} dismissible onDismiss={() => setInfoMessage('')} />}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,420px)_minmax(0,1fr)]">
          <div id="system-settings" ref={systemSettingsRef}>
          <Card className={brandClasses.elevatedCard}>
            <CardBody className="space-y-5">
              <div>
                <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('privacy.title', { ns: 'admin' })}</h2>
                <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>{t('privacy.description', { ns: 'admin' })}</p>
              </div>

              {loadingSettings ? (
                <div className="space-y-3">
                  <div className="h-5 w-40 animate-pulse rounded bg-[#efe9db]" />
                  <div className="h-4 w-full animate-pulse rounded bg-[#f4efe4]" />
                </div>
              ) : settings ? (
                <>
                  <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] p-4">
                    <Checkbox
                      checked={settings.allowLivingPersonPublicDisplay}
                      onChange={(event) => void handleSystemToggle(event.target.checked)}
                      disabled={savingSystemSetting}
                      label={t('privacy.systemToggle', { ns: 'admin' })}
                      helpText={t('privacy.systemHelp', { ns: 'admin' })}
                    />
                  </div>

                  <div className="rounded-2xl border border-[#E2DDD8] bg-[#FFFFFF] px-4 py-3 text-sm">
                    <p className={`font-medium ${brandClasses.primaryText}`}>{t('privacy.lastUpdated', { ns: 'admin' })}</p>
                    <p className={`mt-1 ${brandClasses.bodyText}`}>{formatDateTime(settings.updatedAt)}</p>
                  </div>
                </>
              ) : null}
            </CardBody>
          </Card>
          </div>

          <div id="user-management" ref={userManagementRef}>
          <Card className={brandClasses.elevatedCard}>
            <CardBody className="space-y-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('users.title', { ns: 'admin' })}</h2>
                  <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>{t('users.description', { ns: 'admin' })}</p>
                </div>
                <Button type="button" variant="outline" onClick={() => void loadUsers(searchQuery)} disabled={loadingUsers}>
                  {t('users.refresh', { ns: 'admin' })}
                </Button>
              </div>

              <form onSubmit={handleSearchSubmit} className="flex flex-col gap-3 sm:flex-row">
                <TextInput
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('users.searchPlaceholder', { ns: 'admin' })}
                  className="sm:flex-1"
                />
                <Button type="submit" disabled={loadingUsers}>
                  {t('users.search', { ns: 'admin' })}
                </Button>
              </form>

              {loadingUsers && users.length === 0 ? (
                <div className="space-y-3">
                  <div className="h-4 w-full animate-pulse rounded bg-[#f4efe4]" />
                  <div className="h-4 w-4/5 animate-pulse rounded bg-[#f4efe4]" />
                  <div className="h-4 w-3/5 animate-pulse rounded bg-[#f4efe4]" />
                </div>
              ) : users.length === 0 ? (
                <div className="rounded-2xl border border-[#E2DDD8] bg-[#F9F7F4] px-4 py-6 text-sm text-[#6B6560]">
                  {t('users.empty', { ns: 'admin' })}
                </div>
              ) : (
                <div className="overflow-hidden rounded-2xl border border-[#E2DDD8]">
                  <div className="grid grid-cols-[minmax(0,1.5fr)_120px_170px] gap-4 border-b border-[#E2DDD8] bg-[#F9F7F4] px-4 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#6B6560]">
                    <span>{t('users.columns.user', { ns: 'admin' })}</span>
                    <span>{t('users.columns.role', { ns: 'admin' })}</span>
                    <span>{t('users.columns.permission', { ns: 'admin' })}</span>
                  </div>
                  {users.map((user) => (
                    <div
                      key={user.userId}
                      className="grid grid-cols-[minmax(0,1.5fr)_120px_170px] gap-4 border-t border-[#EDE7E0] px-4 py-4"
                    >
                      <div className="min-w-0">
                        <p className={`truncate font-medium ${brandClasses.primaryText}`}>{user.fullName || user.email}</p>
                        <p className={`truncate text-sm ${brandClasses.bodyText}`}>{user.email}</p>
                      </div>
                      <div className={`text-sm ${brandClasses.bodyText}`}>{user.systemRole}</div>
                      <div>
                          <Checkbox
                          checked={user.canAllowLivingPersonPublicDisplay}
                          onChange={(event) => void handleUserToggle(user.userId, event.target.checked)}
                          disabled={savingUserId === user.userId}
                          label={t('users.permissionLabel', { ns: 'admin' })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};
